// @flow

import * as React from 'react';
import ReactDOM from 'react-dom';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import sumBy from 'lodash/sumBy';
import isEqual from 'lodash/isEqual';
import forIn from 'lodash/forIn';
import { TweenMax, TimelineMax, Power4 } from 'gsap';

type Props = {
  children: any,
  className?: string,
  duration?: number,
};

type State = {
  height: number,
  isAnimating: boolean,
};

function calcYPositions(playerIds, rowHeights) {
  let aggregate = 0;
  return playerIds.reduce((memo, id) => {
    const h = rowHeights[id] || 0;
    aggregate = h + aggregate;
    return Object.assign(memo, {
      [id]: aggregate - h,
    });
  }, {});
}

class AnimatedRowGroup extends React.Component<Props, State> {
  props: Props;
  fromYPositions = {};
  rowRefs = {};
  state: State = {
    height: 0,
    isAnimating: false,
  };

  static defaultProps = {
    className: undefined,
    duration: 2,
  };

  componentDidMount() {
    const rowIds = this.props.children.map(c => c.props.id);
    const yPositions = calcYPositions(rowIds, mapValues(this.rowRefs, r => r.offsetHeight));
    rowIds.forEach((id) => {
      TweenMax.set(this.rowRefs[id], {
        y: yPositions[id],
      });
    });
    this.calculateAndSetHeight();
    this.fromYPositions = yPositions;
  }

  shouldComponentUpdate(nextProps) {
    const isDifferent = !isEqual(
      this.props.children.map(c => c.props.score),
      nextProps.children.map(c => c.props.score),
    );
    return isDifferent || this.state.height === 0;
  }

  componentDidUpdate(prevProps) {
    const prevChildren = prevProps.children.map(c => c.props.score);
    const currentChildren = this.props.children.map(c => c.props.score);
    const update = !isEqual(prevChildren, currentChildren);
    if (update) {
      this.animateRows();
    }
  }

  componentWillUnmount() {
    if (this.tl) this.tl.kill();
  }

  calculateAndSetHeight() {
    this.setState({
      height: sumBy(Object.values(this.rowRefs), r => r.offsetHeight) || 0,
    });
  }

  handleAnimationComplete() {
    this.setState({
      isAnimating: false,
    });
  }

  // use greensock animation to solve iOS animation bugs!
  animateRows() {
    const MIN_ROW_HEIGHT = 61;
    const rowIds = this.props.children.map(c => c.props.id);
    this.rowRefs = pick(this.rowRefs, rowIds);
    const toYPositions = calcYPositions(rowIds, mapValues(this.rowRefs, r => r.offsetHeight));

    if (this.tl) {
      // kill a previous timeline that may have no finished yet due to switching tabs
      this.tl.kill();
    }

    this.tl = new TimelineMax({
      onComplete: this.handleAnimationComplete,
      onCompleteScope: this,
    });

    forIn(rowIds, (id) => {
      const ref = this.rowRefs[id];
      const from =
        this.fromYPositions[id] !== undefined
          ? {
            // this competitor was already in the list, so move it from it's previous position
            y: this.fromYPositions[id],
          }
          : {
            // this competitor has just entered the top 100, so just fade it in
            y: toYPositions[id] + MIN_ROW_HEIGHT,
            opacity: 0,
          };

      const innerTl = new TimelineMax();
      // do the position tween
      innerTl.add(TweenMax.fromTo(ref, this.props.duration, from, {
        y: toYPositions[id],
        opacity: 1,
        ease: Power4.easeInOut,
      }));

      this.tl.add(innerTl, 0);
    });

    this.calculateAndSetHeight();
    this.setState({
      isAnimating: true,
    });

    this.fromYPositions = toYPositions;
  }

  // send props rowGroupIndex and isAnimating to the children in case they are useful...
  render() {
    const { children, className } = this.props;
    const { height, isAnimating } = this.state;
    const { rowRefs } = this;

    return (
      <div
        className={className}
        style={{
          height,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {React.Children.map(children, (child, index) => {
          const cloneProps = {
            ...child.props,
            ref: (node) => {
              rowRefs[child.props.id] = ReactDOM.findDOMNode(node);
              if (child.ref) {
                child.ref(node);
              }
            },
            rowGroupIndex: index,
            isAnimating,
            style: { zIndex: children.length - index },
          };
          return React.cloneElement(child, cloneProps);
        })}
      </div>
    );
  }
}

export default AnimatedRowGroup;
